import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import DatePicker from 'react-datepicker';
import TaskChecklist from './TaskChecklist';
import api from '../../utils/api';
import 'react-datepicker/dist/react-datepicker.css';
import './EventForm.css';

const WEEKDAYS = [
  { value: 0, short: 'S', name: 'Sun' },
  { value: 1, short: 'M', name: 'Mon' },
  { value: 2, short: 'T', name: 'Tue' },
  { value: 3, short: 'W', name: 'Wed' },
  { value: 4, short: 'T', name: 'Thu' },
  { value: 5, short: 'F', name: 'Fri' },
  { value: 6, short: 'S', name: 'Sat' }
];

function timeSlots30() {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
    slots.push(`${String(h).padStart(2, '0')}:30`);
  }
  return slots;
}

const TIME_SLOTS = timeSlots30();

const mapCleaningType = (serviceType, cleaningCategory) => {
  if (serviceType === 'commercial') return 'commercial';
  if (serviceType === 'moveinout') return 'move';
  if (cleaningCategory === 'main') return 'deep';
  return 'residential';
};

function CollapsibleSection({ title, description, defaultExpanded = false, children }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div className="ef-section">
      <div className="ef-section-header" onClick={() => setExpanded(!expanded)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}>
        <div>
          <span className="ef-section-title">{title}</span>
          {description && <span className="ef-section-desc">{description}</span>}
        </div>
        <span className={`ef-section-arrow ${expanded ? 'ef-section-arrow-open' : ''}`}>&#9660;</span>
      </div>
      {expanded && <div className="ef-section-body">{children}</div>}
    </div>
  );
}

// Format a Date to YYYY-MM-DD using local date components (avoids UTC off-by-one)
function toLocalDateString(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function EventForm({ onSubmit, onCancel, isAdmin, clients = [], initialClientId = '', initialDate = null }) {
  const { t, language } = useLanguage();
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState(() => {
    if (!isAdmin) {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        return userData?.address || '';
      } catch { return ''; }
    }
    return '';
  });
  const [startDate, setStartDate] = useState(initialDate || new Date());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('09:30');
  const [allDay, setAllDay] = useState(false);
  const [recurring, setRecurring] = useState(false);
  const [recurrenceEvery, setRecurrenceEvery] = useState(1);
  const [recurrenceDays, setRecurrenceDays] = useState([]);
  const [recurrenceUntil, setRecurrenceUntil] = useState(null);
  const [clientId, setClientId] = useState(initialClientId || '');
  const [checklist, setChecklist] = useState([]);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // --- Order configuration state (matching mobile app) ---
  const [serviceType, setServiceType] = useState('home');
  const [cleaningCategory, setCleaningCategory] = useState('standard');
  const [asNeededSelections, setAsNeededSelections] = useState([]);
  const [mainCleaningExtras, setMainCleaningExtras] = useState([]);
  const [adhocSelections, setAdhocSelections] = useState([]);
  const [adhocFreeText, setAdhocFreeText] = useState('');
  const [extraBathrooms, setExtraBathrooms] = useState(0);
  const [homeSize, setHomeSize] = useState('under150');
  const [extraTargeted, setExtraTargeted] = useState({});
  const [equipment, setEquipment] = useState({});
  const [manualHours, setManualHours] = useState(3);

  // --- Configurable options (loaded from backend, fallback to defaults) ---
  const [sectionTitles, setSectionTitles] = useState({
    serviceType: t('newOrder.serviceType', 'Service Type'),
    cleaningCategory: t('newOrder.cleaningCategory', 'Cleaning Category'),
    alwaysIncluded: t('newOrder.alwaysIncluded', 'Always Included'),
    asNeeded: t('newOrder.asNeeded', 'As Needed'),
    extraTargeted: t('newOrder.extraTargeted', 'Extra Targeted Cleaning'),
    equipment: t('newOrder.equipment', 'Equipment'),
    bookingDetails: t('newOrder.bookingDetails', 'Booking Details'),
  });
  // Section subtitles mirror the descriptions admin edits in Order Config.
  const [sectionDescriptions, setSectionDescriptions] = useState({
    serviceType: '',
    cleaningCategory: '',
    alwaysIncluded: '',
    asNeeded: '',
    extraTargeted: '',
    equipment: '',
    bookingDetails: '',
  });
  const [bookingDetailsLabels, setBookingDetailsLabels] = useState({
    service: t('scheduling.service', 'Service'),
    servicePlaceholder: t('scheduling.titlePlaceholder', 'e.g. Cleaning service'),
    client: t('admin.client', 'Client'),
    chooseClient: t('admin.chooseClient', 'Choose a client...'),
    startDate: t('scheduling.startDate', 'Start date'),
    startTime: t('scheduling.startTime', 'Start time'),
    endTime: t('scheduling.endTime', 'End time'),
    to: t('common.to', 'To'),
    allDay: t('scheduling.allDay', 'All day'),
    recurring: t('scheduling.recurring', 'Recurring'),
    repeatEvery: t('scheduling.repeatEvery', 'Repeat every'),
    weekSuffix: t('scheduling.week', 'week(s)'),
    onDays: t('scheduling.onDays', 'On days'),
    untilDate: t('scheduling.untilDate', 'Until date'),
    untilDateOptional: t('scheduling.optional', 'optional'),
    untilDatePlaceholder: t('scheduling.selectUntilOptional', 'Leave blank to repeat for 1 year'),
    address: t('auth.address', 'Address'),
    whatNeedsToBeDone: t('client.whatNeedsToBeDone', 'What needs to be done'),
    checklistPlaceholder: t('client.checklistPlaceholder', 'e.g. Kitchen cleaning, Bathroom deep clean...'),
    comments: t('client.comments', 'Comments'),
    saveEvent: t('scheduling.saveEvent', 'Save'),
    submitting: t('common.submitting', 'Submitting...'),
    cancel: t('common.cancel', 'Cancel'),
  });
  const [serviceTypeOptions, setServiceTypeOptions] = useState([
    { value: 'home', label: t('newOrder.homeCleaning', 'Home Cleaning') },
    { value: 'commercial', label: t('newOrder.commercialCleaning', 'Commercial Cleaning') },
    { value: 'moveinout', label: t('newOrder.moveInOut', 'Move-in/Move-out') },
  ]);
  const [cleaningCategoryOptions, setCleaningCategoryOptions] = useState([
    { value: 'standard', label: t('newOrder.standardCleaning', 'Standard Cleaning'), description: t('newOrder.standardCleaningDesc', '3 hours — regular maintenance cleaning') },
    { value: 'main', label: t('newOrder.mainCleaning', 'Main Cleaning'), description: t('newOrder.mainCleaningDesc', '5-6 hours — deep thorough cleaning') },
    { value: 'adhoc', label: t('newOrder.adhocCleaning', 'Ad hoc Cleaning'), description: t('newOrder.adhocCleaningDesc', 'Custom cleaning based on your selection') },
  ]);
  const [alwaysIncludedItems, setAlwaysIncludedItems] = useState([
    t('newOrder.vacuumCleaning', 'Vacuum cleaning'),
    t('newOrder.floorWashing', 'Floor washing'),
    t('newOrder.wipingSurfaces', 'Wiping surfaces'),
    t('newOrder.bridgeSink', 'Bridge/sink'),
    t('newOrder.bathroom', 'Bathroom, including cleaning toilet and sink (remember plug), mirror and shower'),
    t('newOrder.kitchenSurfaces', 'Kitchen surfaces (no washing up)'),
  ]);
  const [asNeededItems, setAsNeededItems] = useState([
    { key: 'wipingFrames', label: t('newOrder.wipingFrames', 'Wiping frames, skirting boards, doors') },
    { key: 'limeShower', label: t('newOrder.limeShower', 'Lime shower cabin and sanitary') },
    { key: 'vacuumFurniture', label: t('newOrder.vacuumFurniture', 'Vacuuming furniture cushions and under cushions') },
    { key: 'cobwebs', label: t('newOrder.cobwebs', 'Cobwebs: walls, ceiling edges and windows') },
  ]);
  const mainExtraItems = [
    { key: 'wipingFrames', label: t('newOrder.wipingFrames', 'Wiping frames, skirting boards, doors') },
    { key: 'wipingWindowEdges', label: t('newOrder.wipingWindowEdges', 'Wiping window edges') },
    { key: 'wipingCeilingEdges', label: t('newOrder.wipingCeilingEdges', 'Wiping ceiling edges') },
    { key: 'windowCleaning', label: t('newOrder.windowCleaning', 'Window cleaning (if necessary)') },
    { key: 'blindsCleaned', label: t('newOrder.blindsCleaned', 'Blinds cleaned') },
    { key: 'furnitureVacuumed', label: t('newOrder.furnitureVacuumed', 'Furniture thoroughly vacuumed') },
    { key: 'behindFurniture', label: t('newOrder.behindFurniture', 'Behind furniture') },
    { key: 'cleaningWallsCeilings', label: t('newOrder.cleaningWallsCeilings', 'Cleaning walls and ceilings') },
    { key: 'underCarpets', label: t('newOrder.underCarpets', 'Under loose carpets') },
    { key: 'mirrorsGlass', label: t('newOrder.mirrorsGlass', 'Mirrors and glass surfaces') },
    { key: 'ceilingLights', label: t('newOrder.ceilingLights', 'Cleaning ceiling lights') },
    { key: 'closedSpaces', label: t('newOrder.closedSpaces', 'More closed spaces (if necessary)') },
    { key: 'insideCupboards', label: t('newOrder.insideCupboards', 'Inside cupboards, where dust and dirt collect') },
    { key: 'woodenFloors', label: t('newOrder.woodenFloors', 'Wooden floors washed and polished') },
    { key: 'ovenFridge', label: t('newOrder.ovenFridge', 'Oven and fridge') },
  ];
  const adhocPredefined = [
    { key: 'standardWithout', label: t('newOrder.standardWithout', 'Standard without bathroom or floor washing (2.5 hours)') },
    { key: 'vacuumAndFloor', label: t('newOrder.vacuumAndFloor', 'Only vacuuming and floor washing (2 hours)') },
    { key: 'bathroomAndFloor', label: t('newOrder.bathroomAndFloor', 'Only bathroom and floor washing or vacuuming (2 hours)') },
  ];
  const homeSizeOptions = [
    { value: 'under150', label: t('newOrder.under150', 'Under 150 sq m (5 hours)') },
    { value: 'over150', label: t('newOrder.over150', 'Over 150 sq m (6 hours)') },
  ];
  const [extraTargetedItems, setExtraTargetedItems] = useState([
    { key: 'animalHair', label: t('newOrder.animalHair', 'Animal hair') },
    { key: 'smoking', label: t('newOrder.smoking', 'Smoking') },
  ]);
  const [equipmentItems, setEquipmentItems] = useState([
    { key: 'cleaningAgents', label: t('newOrder.cleaningAgents', 'Cleaning agents') },
    { key: 'cloth', label: t('newOrder.cloth', 'Cloth') },
    { key: 'vacuumCleaner', label: t('newOrder.vacuumCleaner', 'Vacuum cleaner') },
    { key: 'mop', label: t('newOrder.mop', 'Mop') },
    { key: 'specialProducts', label: t('newOrder.specialProducts', 'Special products') },
  ]);
  // Admin-created sections loaded from backend. Selections are tracked in a
  // flat Set of `${sectionId}:${itemKey}` strings for easy toggling/serializing.
  const [customSections, setCustomSections] = useState([]);
  const [customSelections, setCustomSelections] = useState(new Set());
  const toggleCustomSelection = (sectionId, itemKey) => {
    const token = `${sectionId}:${itemKey}`;
    setCustomSelections(prev => {
      const next = new Set(prev);
      if (next.has(token)) next.delete(token); else next.add(token);
      return next;
    });
  };

  const isHome = serviceType === 'home';
  const selectedServiceTypeLabel = useMemo(() => {
    const selected = serviceTypeOptions.find((option) => option.value === serviceType);
    return selected?.label || serviceType || t('scheduling.titlePlaceholder', 'Cleaning service');
  }, [serviceTypeOptions, serviceType, t]);

  // Load order config from backend, translated to the current UI language.
  // Re-fetches when the user toggles the language dropdown.
  useEffect(() => {
    api.get(`/order-config?lang=${encodeURIComponent(language)}`).then(res => {
      const data = res.data?.data;
      if (data) {
        if (data.sectionTitles) setSectionTitles(prev => ({ ...prev, ...data.sectionTitles }));
        if (data.sectionDescriptions) setSectionDescriptions(prev => ({ ...prev, ...data.sectionDescriptions }));
        if (Array.isArray(data.serviceTypeOptions)) setServiceTypeOptions(data.serviceTypeOptions);
        if (Array.isArray(data.cleaningCategoryOptions)) setCleaningCategoryOptions(data.cleaningCategoryOptions);
        if (Array.isArray(data.alwaysIncludedItems)) setAlwaysIncludedItems(data.alwaysIncludedItems);
        if (Array.isArray(data.asNeededItems)) setAsNeededItems(data.asNeededItems);
        if (Array.isArray(data.extraTargetedItems)) setExtraTargetedItems(data.extraTargetedItems);
        if (Array.isArray(data.equipmentItems)) setEquipmentItems(data.equipmentItems);
        if (Array.isArray(data.customSections)) setCustomSections(data.customSections);
        if (data.bookingDetailsLabels) setBookingDetailsLabels(prev => ({ ...prev, ...data.bookingDetailsLabels }));
      }
    }).catch(() => {});
  }, [language]);

  // Keep the toggle state maps in sync with the (possibly reloaded) item lists
  useEffect(() => {
    setExtraTargeted(prev => {
      const next = {};
      extraTargetedItems.forEach(item => { next[item.key] = !!prev[item.key]; });
      return next;
    });
  }, [extraTargetedItems]);

  useEffect(() => {
    setEquipment(prev => {
      const next = {};
      equipmentItems.forEach(item => { next[item.key] = !!prev[item.key]; });
      return next;
    });
  }, [equipmentItems]);

  // When admin selects a client, pre-fill address from that client's data
  useEffect(() => {
    if (isAdmin && clientId) {
      const selected = clients.find(c => String(c._id || c.id) === String(clientId));
      if (selected?.address) setAddress(selected.address);
    }
  }, [isAdmin, clientId, clients]);

  useEffect(() => {
    if (!serviceType) return;
    setTitle(selectedServiceTypeLabel);
  }, [serviceType, selectedServiceTypeLabel]);

  const toggleAsNeeded = (key) => setAsNeededSelections(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const toggleMainExtra = (key) => setMainCleaningExtras(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const toggleAdhoc = (key) => setAdhocSelections(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const toggleEquipment = (key) => setEquipment(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleExtraTargeted = (key) => setExtraTargeted(prev => ({ ...prev, [key]: !prev[key] }));

  const buildChecklist = () => {
    const items = [];
    if (asNeededSelections.length) items.push(...asNeededSelections);
    if (mainCleaningExtras.length) items.push(...mainCleaningExtras);
    if (adhocSelections.length) items.push(...adhocSelections);
    Object.entries(extraTargeted).forEach(([key, val]) => { if (val) items.push(key); });
    Object.entries(equipment).forEach(([key, val]) => { if (val) items.push(`equipment:${key}`); });
    customSelections.forEach((token) => items.push(`custom:${token}`));
    if (Array.isArray(checklist)) items.push(...checklist.filter(Boolean));
    return [...new Set(items)];
  };

  const calculateHours = () => {
    if (!isHome) return manualHours || 3;
    if (cleaningCategory === 'standard') return 3;
    if (cleaningCategory === 'main') return homeSize === 'over150' ? 6 : 5;
    if (cleaningCategory === 'adhoc') {
      let hrs = 0;
      if (adhocSelections.includes('standardWithout')) hrs = Math.max(hrs, 2.5);
      if (adhocSelections.includes('vacuumAndFloor')) hrs = Math.max(hrs, 2);
      if (adhocSelections.includes('bathroomAndFloor')) hrs = Math.max(hrs, 2);
      if (hrs === 0) hrs = 2;
      hrs += extraBathrooms * 0.5;
      if (adhocSelections.includes('largeHome')) hrs += 0.5;
      return hrs;
    }
    return 3;
  };

  const toggleRecurrenceDay = (d) => {
    setRecurrenceDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a, b) => a - b));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!address.trim()) {
      setError(t('client.addressRequired', 'Address is required'));
      return;
    }
    if (isAdmin && !clientId) {
      setError(t('admin.pleaseSelectClient', 'Please select a client.'));
      return;
    }
    if (!allDay && !startTime) {
      setError(t('client.timeRequired', 'Start time is required'));
      return;
    }
    const end = allDay ? null : endTime;
    const estimatedHours = calculateHours();
    const hours = allDay ? 8 : (() => {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = (end || startTime).split(':').map(Number);
      const timeHours = Math.max(0.5, (eh * 60 + em - (sh * 60 + sm)) / 60);
      return Math.max(timeHours, estimatedHours);
    })();
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim() || (address.trim().slice(0, 50)),
        address: address.trim(),
        date: toLocalDateString(startDate),
        time: allDay ? '09:00' : startTime,
        endTime: end,
        hours,
        allDay,
        cleaningType: mapCleaningType(serviceType, cleaningCategory),
        frequency: (recurring && recurrenceDays.length) ? 'weekly' : 'once',
        comments: comments.trim(),
        checklist: buildChecklist()
      };
    if (recurring && recurrenceDays.length) {
      payload.recurrenceDays = recurrenceDays;
      payload.recurrenceUntil = recurrenceUntil
        ? toLocalDateString(typeof recurrenceUntil === 'string' ? new Date(recurrenceUntil) : recurrenceUntil)
        : null;
      payload.recurrenceEvery = recurrenceEvery;
    }
      if (isAdmin) payload.clientId = clientId;
      await onSubmit(payload);
    } catch (err) {
      setError(err.response?.data?.message || t('client.errorSubmitting', 'Error submitting request.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="event-form" onSubmit={handleSubmit}>

      {/* ===== Section: Service Type ===== */}
      <CollapsibleSection title={sectionTitles.serviceType} description={sectionDescriptions.serviceType} defaultExpanded>
        <div className="ef-radio-group">
          {serviceTypeOptions.map(opt => (
            <label key={opt.value} className={`ef-radio-option ${serviceType === opt.value ? 'ef-radio-active' : ''}`}>
              <input type="radio" name="serviceType" value={opt.value} checked={serviceType === opt.value} onChange={() => setServiceType(opt.value)} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </CollapsibleSection>

      {/* ===== Section: Cleaning Category (Home only) ===== */}
      {isHome && (
        <CollapsibleSection title={sectionTitles.cleaningCategory} description={sectionDescriptions.cleaningCategory}>
          <div className="ef-radio-group">
            {cleaningCategoryOptions.map(opt => (
              <label key={opt.value} className={`ef-radio-option ${cleaningCategory === opt.value ? 'ef-radio-active' : ''}`}>
                <input type="radio" name="cleaningCategory" value={opt.value} checked={cleaningCategory === opt.value} onChange={() => setCleaningCategory(opt.value)} />
                <span className="ef-radio-label">{opt.label}</span>
                {opt.description && <span className="ef-radio-desc">{opt.description}</span>}
              </label>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ===== Always Included (standard/main) ===== */}
      {isHome && (cleaningCategory === 'standard' || cleaningCategory === 'main') && (
        <CollapsibleSection title={sectionTitles.alwaysIncluded} description={sectionDescriptions.alwaysIncluded}>
          <ul className="ef-checklist-display">
            {alwaysIncludedItems.map((item, i) => (
              <li key={i}><span className="ef-check-icon">✓</span>{item}</li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* ===== As Needed (standard only) ===== */}
      {isHome && cleaningCategory === 'standard' && (
        <CollapsibleSection title={sectionTitles.asNeeded} description={sectionDescriptions.asNeeded}>
          <div className="ef-checkbox-group">
            {asNeededItems.map(item => (
              <label key={item.key} className="ef-checkbox-option">
                <input type="checkbox" checked={asNeededSelections.includes(item.key)} onChange={() => toggleAsNeeded(item.key)} />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ===== Main Cleaning Extras (main only) ===== */}
      {isHome && cleaningCategory === 'main' && (
        <>
          <CollapsibleSection title={t('newOrder.mainCleaningExtras', 'Main Cleaning — Additional Tasks')} description={t('newOrder.mainCleaningExtrasDesc', 'All standard items plus these extra tasks')}>
            <div className="ef-checkbox-group">
              {mainExtraItems.map(item => (
                <label key={item.key} className="ef-checkbox-option">
                  <input type="checkbox" checked={mainCleaningExtras.includes(item.key)} onChange={() => toggleMainExtra(item.key)} />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </CollapsibleSection>
          <CollapsibleSection title={t('newOrder.homeSize', 'Home Size')}>
            <div className="ef-radio-group">
              {homeSizeOptions.map(opt => (
                <label key={opt.value} className={`ef-radio-option ${homeSize === opt.value ? 'ef-radio-active' : ''}`}>
                  <input type="radio" name="homeSize" value={opt.value} checked={homeSize === opt.value} onChange={() => setHomeSize(opt.value)} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* ===== Ad hoc Options (adhoc only) ===== */}
      {isHome && cleaningCategory === 'adhoc' && (
        <CollapsibleSection title={t('newOrder.adhocOptions', 'Ad hoc Cleaning Options')} description={t('newOrder.adhocOptionsDesc', 'Select the cleaning you need')}>
          <div className="ef-checkbox-group">
            {adhocPredefined.map(item => (
              <label key={item.key} className="ef-checkbox-option">
                <input type="checkbox" checked={adhocSelections.includes(item.key)} onChange={() => toggleAdhoc(item.key)} />
                <span>{item.label}</span>
              </label>
            ))}
            <label className="ef-checkbox-option">
              <input type="checkbox" checked={extraBathrooms > 0} onChange={(e) => setExtraBathrooms(e.target.checked ? 1 : 0)} />
              <span>{t('newOrder.moreBathrooms', 'More bathrooms (+½ hour each)')}</span>
            </label>
            {extraBathrooms > 0 && (
              <div className="ef-inline-input">
                <label>{t('newOrder.bathroomCount', 'Number of extra bathrooms')}</label>
                <input type="number" min="1" value={extraBathrooms} onChange={(e) => setExtraBathrooms(Math.max(0, parseInt(e.target.value) || 0))} className="form-control ef-small-input" />
              </div>
            )}
            <label className="ef-checkbox-option">
              <input type="checkbox" checked={adhocSelections.includes('largeHome')} onChange={() => toggleAdhoc('largeHome')} />
              <span>{t('newOrder.largeHome', 'House over 150 sq m with 3+ rooms (+½ hour)')}</span>
            </label>
          </div>
          <div className="event-form-row" style={{ marginTop: 10 }}>
            <label>{t('newOrder.specialRequests', 'Special Requests')}</label>
            <textarea value={adhocFreeText} onChange={(e) => setAdhocFreeText(e.target.value)} rows={2} className="form-control" placeholder={t('newOrder.specialRequestsPlaceholder', 'Describe any additional cleaning needs...')} />
          </div>
        </CollapsibleSection>
      )}

      {/* ===== Commercial / Move-in-out ===== */}
      {!isHome && (
        <CollapsibleSection title={serviceType === 'commercial' ? t('newOrder.commercialCleaning', 'Commercial Cleaning') : t('newOrder.moveInOut', 'Move-in/Move-out')}>
          <div className="event-form-row">
            <label>{serviceType === 'commercial' ? t('newOrder.commercialDesc', 'Describe your commercial cleaning needs') : t('newOrder.moveInOutDesc', 'Describe your move-in/move-out cleaning needs')}</label>
            <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className="form-control" placeholder={t('newOrder.commentsPlaceholder', 'Any other notes or instructions...')} />
          </div>
          <div className="event-form-row">
            <label>{t('newOrder.hoursLabel', 'Hours needed')}</label>
            <input type="number" min="0.5" step="0.5" value={manualHours} onChange={(e) => setManualHours(parseFloat(e.target.value) || 0)} className="form-control ef-small-input" />
          </div>
        </CollapsibleSection>
      )}

      {/* ===== Section: Extra Targeted Cleaning ===== */}
      {extraTargetedItems.length > 0 && (
        <CollapsibleSection title={sectionTitles.extraTargeted} description={sectionDescriptions.extraTargeted}>
          <div className="ef-checkbox-group">
            {extraTargetedItems.map(item => (
              <label key={item.key} className="ef-checkbox-option">
                <input type="checkbox" checked={!!extraTargeted[item.key]} onChange={() => toggleExtraTargeted(item.key)} />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ===== Section: Equipment ===== */}
      {equipmentItems.length > 0 && (
        <CollapsibleSection title={sectionTitles.equipment} description={sectionDescriptions.equipment}>
          <div className="ef-checkbox-group">
            {equipmentItems.map(item => (
              <label key={item.key} className="ef-checkbox-option">
                <input type="checkbox" checked={!!equipment[item.key]} onChange={() => toggleEquipment(item.key)} />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ===== Custom admin-defined sections ===== */}
      {customSections.map((sec) => (
        sec.items && sec.items.length > 0 && (
          <CollapsibleSection key={sec.id} title={sec.title} description={sec.description}>
            <div className="ef-checkbox-group">
              {sec.items.map((item) => {
                const token = `${sec.id}:${item.key}`;
                return (
                  <label key={item.key} className="ef-checkbox-option">
                    <input
                      type="checkbox"
                      checked={customSelections.has(token)}
                      onChange={() => toggleCustomSelection(sec.id, item.key)}
                    />
                    <span>{item.label}</span>
                  </label>
                );
              })}
            </div>
          </CollapsibleSection>
        )
      ))}

      {/* ===== Section: Booking Details ===== */}
      <CollapsibleSection title={sectionTitles.bookingDetails} description={sectionDescriptions.bookingDetails} defaultExpanded>

      <div className="event-form-row">
        <label>{bookingDetailsLabels.service}</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={bookingDetailsLabels.servicePlaceholder}
          className="form-control"
        />
      </div>

      {isAdmin && (
        <div className="event-form-row">
          <label>{bookingDetailsLabels.client} *</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} required className="form-control">
            <option value="">{bookingDetailsLabels.chooseClient}</option>
            {clients.map(c => (
              <option key={c._id || c.id} value={c._id || c.id}>{c.name} {c.email ? `(${c.email})` : ''}</option>
            ))}
          </select>
        </div>
      )}

      <div className="event-form-row">
        <label>{bookingDetailsLabels.startDate} *</label>
        <DatePicker
          selected={startDate}
          onChange={(d) => setStartDate(d)}
          minDate={new Date()}
          dateFormat="dd/MM/yyyy"
          className="form-control date-picker-input"
          required
        />
      </div>

      <div className="event-form-row event-form-row-inline">
        <div className="event-form-group">
          <label>{bookingDetailsLabels.startTime}</label>
          <select
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            disabled={allDay}
            className="form-control"
          >
            {TIME_SLOTS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <span className="event-form-to">{bookingDetailsLabels.to}</span>
        <div className="event-form-group">
          <label>{bookingDetailsLabels.endTime}</label>
          <select
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            disabled={allDay}
            className="form-control"
          >
            {TIME_SLOTS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="event-form-row event-form-checkbox">
        <label>
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          {bookingDetailsLabels.allDay}
        </label>
      </div>

      <div className="event-form-row event-form-checkbox">
        <label>
          <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
          {bookingDetailsLabels.recurring}
        </label>
      </div>

      {recurring && (
        <div className="event-form-recurring">
          <div className="event-form-row event-form-row-inline">
            <label>{bookingDetailsLabels.repeatEvery}</label>
            <input
              type="number"
              min={1}
              max={52}
              value={recurrenceEvery}
              onChange={(e) => setRecurrenceEvery(parseInt(e.target.value, 10) || 1)}
              className="form-control event-form-number"
            />
            <span>{bookingDetailsLabels.weekSuffix}</span>
          </div>
          <div className="event-form-row">
            <label>{bookingDetailsLabels.onDays}</label>
            <div className="event-form-weekdays">
              {WEEKDAYS.map(({ value, short }) => (
                <button
                  key={value}
                  type="button"
                  className={`event-form-weekday ${recurrenceDays.includes(value) ? 'active' : ''}`}
                  onClick={() => toggleRecurrenceDay(value)}
                  title={WEEKDAYS[value].name}
                >
                  {short}
                </button>
              ))}
            </div>
          </div>
          <div className="event-form-row">
            <label>{bookingDetailsLabels.untilDate} ({bookingDetailsLabels.untilDateOptional})</label>
            <DatePicker
              selected={recurrenceUntil}
              onChange={(d) => setRecurrenceUntil(d)}
              minDate={startDate}
              dateFormat="dd/MM/yyyy"
              placeholderText={bookingDetailsLabels.untilDatePlaceholder}
              className="form-control date-picker-input"
            />
          </div>
        </div>
      )}

      <div className="event-form-row">
        <label>{bookingDetailsLabels.address} *</label>
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          required
          className="form-control"
        />
      </div>

      <div className="event-form-row">
        <TaskChecklist
          items={checklist}
          onChange={setChecklist}
          placeholder={bookingDetailsLabels.checklistPlaceholder}
          label={bookingDetailsLabels.whatNeedsToBeDone}
        />
      </div>

      <div className="event-form-row">
        <label>{bookingDetailsLabels.comments}</label>
        <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={2} className="form-control" />
      </div>

      </CollapsibleSection>

      {error && <div className="event-form-error">{error}</div>}

      <div className="event-form-actions">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? bookingDetailsLabels.submitting : bookingDetailsLabels.saveEvent}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {bookingDetailsLabels.cancel}
        </button>
      </div>
    </form>
  );
}
